import { useState, useCallback } from 'react';
import { useToast, useDisclosure } from '@chakra-ui/react';
import requestApi from 'utils/Request';
import { GROUPS } from 'utils/StaticApiEndpoints';

const useMerchantGroups = () => {
  const [merchantGroups, setMerchantGroups] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [isFetchingGroups, setIsFetchingGroups] = useState(false);
  const [isCreatingGroup, setIsCreatingGroup] = useState(false);
  const [isUpdatingGroup, setIsUpdatingGroup] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [groupModalData, setGroupModalData] = useState({
    name: '',
    merchants: [],
    isEdit: false,
    editId: null,
    type: null,
  });

  const {
    isOpen: isGroupModalOpen,
    onOpen: openGroupModal,
    onClose: closeGroupModal,
  } = useDisclosure();

  const toast = useToast();

  const fetchGroups = useCallback(
    async (type = null) => {
      setIsFetchingGroups(true);
      try {
        const params = type ? { type } : {};
        const response = await requestApi.get(GROUPS, { params });
        setMerchantGroups(response.data || []);
      } catch (error) {
        toast({
          title: 'Error',
          description: 'Failed to fetch merchant groups',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
      } finally {
        setIsFetchingGroups(false);
      }
    },
    [toast],
  );

  const createGroup = async (groupData, type = null) => {
    setIsCreatingGroup(true);
    try {
      const response = await requestApi.post(GROUPS, groupData);
      await fetchGroups(type); // This won't show loader on create/update buttons
      return response;
    } catch (error) {
      toast({
        title: 'Error',
        description: error?.message || 'Failed to create merchant group',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      throw error;
    } finally {
      setIsCreatingGroup(false);
    }
  };

  const updateGroup = async (groupId, groupData, type = null) => {
    setIsUpdatingGroup(true);
    try {
      const response = await requestApi.put(`${GROUPS}/${groupId}`, groupData);
      await fetchGroups(type); // This won't show loader on create/update buttons
      return response;
    } catch (error) {
      toast({
        title: 'Error',
        description: error?.message || 'Failed to update merchant group',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      throw error;
    } finally {
      setIsUpdatingGroup(false);
    }
  };

  const handleGroupSelection = (
    group,
    pendingSelectedMerchant,
    setPendingSelectedMerchant,
  ) => {
    const isGroupSelected = selectedGroup?.id === group.id;

    if (isGroupSelected) {
      // Unselect the current group
      setSelectedGroup(null);
      const groupMerchantIds = group.merchants.map((m) => m.merchantId);
      setPendingSelectedMerchant(
        pendingSelectedMerchant.filter(
          (merchant) => !groupMerchantIds.includes(merchant.merchantId),
        ),
      );
    } else {
      setSelectedGroup(group);
      // Clear all manually selected merchants and select only group merchants
      setPendingSelectedMerchant(group.merchants || []);
    }
  };

  const openCreateGroupModal = (type = null) => {
    setGroupModalData({
      name: '',
      merchants: [],
      isEdit: false,
      editId: null,
      type,
    });
    openGroupModal();
  };

  const openEditGroupModal = (group, type = null) => {
    setGroupModalData({
      name: group.groupName,
      merchants: group.merchants,
      isEdit: true,
      editId: group.id,
      type,
    });
    openGroupModal();
  };

  const handleGroupModalSubmit = async () => {
    if (!groupModalData.name.trim()) {
      toast({
        title: 'Error',
        description: 'Group name is required',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    if (groupModalData.merchants.length === 0) {
      toast({
        title: 'Error',
        description: 'Please select at least one merchant',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      const groupPayload = {
        name: groupModalData.name,
        merchantIds: groupModalData.merchants.map((m) => m.merchantId),
        ...(groupModalData.type ? { type: groupModalData.type } : {}),
      };

      if (groupModalData.isEdit) {
        await updateGroup(
          groupModalData.editId,
          groupPayload,
          groupModalData.type,
        );
      } else {
        await createGroup(groupPayload, groupModalData.type);
      }

      closeGroupModal();
      setSelectedGroup(null);
    } catch (error) {
      // Error handling is done in the API functions
    }
  };

  const handleGroupModalCancel = () => {
    setGroupModalData({
      name: '',
      merchants: [],
      isEdit: false,
      editId: null,
      type: null,
    });
    closeGroupModal();
  };

  const handleGroupMerchantSelection = useCallback((merchant, isSelected) => {
    if (isSelected) {
      setGroupModalData((prev) => ({
        ...prev,
        merchants: [...prev.merchants, merchant],
      }));
    } else {
      setGroupModalData((prev) => ({
        ...prev,
        merchants: prev.merchants.filter(
          (m) => m.merchantId !== merchant.merchantId,
        ),
      }));
    }
  }, []);

  const updateGroupModalData = useCallback((updater) => {
    setGroupModalData(updater);
  }, []);

  const deleteGroup = async (groupId, type = null) => {
    setIsDeleting(true);
    try {
      await requestApi.delete(`${GROUPS}/${groupId}`);
      await fetchGroups(type);
      // Clear selected group if the deleted group was selected
      if (selectedGroup?.id === groupId) {
        setSelectedGroup(null);
      }
    } catch (error) {
      toast({
        title: 'Error',
        description: error?.message || 'Failed to delete merchant group',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      throw error;
    } finally {
      setIsDeleting(false);
    }
  };

  const handleGroupModalDelete = async () => {
    if (groupModalData.editId) {
      try {
        await deleteGroup(groupModalData.editId, groupModalData.type);
        closeGroupModal();
      } catch (error) {
        // Error handling is done in deleteGroup function
      }
    }
  };

  return {
    merchantGroups,
    selectedGroup,
    setSelectedGroup,
    isFetchingGroups,
    isCreatingGroup,
    isUpdatingGroup,
    isDeleting,
    groupModalData,
    setGroupModalData: updateGroupModalData,
    isGroupModalOpen,
    openGroupModal,
    closeGroupModal,
    fetchGroups,
    createGroup,
    updateGroup,
    deleteGroup,
    handleGroupSelection,
    openCreateGroupModal,
    openEditGroupModal,
    handleGroupModalSubmit,
    handleGroupModalCancel,
    handleGroupModalDelete,
    handleGroupMerchantSelection,
  };
};

export default useMerchantGroups;
